import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Logo, TAGLINE } from "../components/Logo";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, ArrowRight } from "lucide-react";

const ROLES = [
  { id: "student", label: "Student" },
  { id: "teacher", label: "Teacher" },
  { id: "parent", label: "Parent" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role: "student",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}! Your account is ready.`);
      nav(`/dashboard/${res.user.role}`);
    } else {
      setErr(res.error);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 ym-grid-bg opacity-30" />
        <Logo inverted />
        <div className="relative">
          <h2 className="font-heading font-black text-4xl leading-tight">
            Join thousands<br />rising together.
          </h2>
          <p className="mt-4 text-blue-100 italic">"{TAGLINE}"</p>
        </div>
        <div className="relative text-blue-100 text-sm">© Young Minds Smart Learning Platform</div>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="lg:hidden mb-6"><Logo /></div>
        <div className="max-w-md w-full mx-auto">
          <h1 className="font-heading font-bold text-3xl text-slate-900">Create your account</h1>
          <p className="mt-2 text-slate-600">It only takes a minute.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-sm font-semibold text-slate-700">I am a</label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => setForm((f) => ({ ...f, role: r.id }))}
                    data-testid={`role-${r.id}`}
                    className={`px-3 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                      form.role === r.id
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700">Full name</label>
              <div className="mt-1.5 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  data-testid="register-name"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="register-email"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700">Password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    required
                    type="password"
                    minLength={6}
                    value={form.password}
                    onChange={set("password")}
                    data-testid="register-password"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Phone</label>
                <div className="mt-1.5 relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="register-phone"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                </div>
              </div>
            </div>

            {err && (
              <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? "Creating account..." : "Create account"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-login" className="font-semibold text-blue-600 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
